// menuRoutes.js
// -----------------------------------------------------------------------------
// The one table of navigable pages per role. Three things read it and they must
// not be able to disagree:
//
//   mainMenu.vue   draws the app bar and the drawer from it
//   index.vue      draws the home launcher cards from it
//   router.js      refuses a page this table says the role may not open
//
// It was one table copied three times before -- adminRoutes / deliveryRoutes /
// userRoutes inside mainMenu.vue -- which is why `manager` silently had no menu
// at all while router.js was happily authorizing it for three staff pages.
//
// Pure: no Vue, no Vuex, no Firestore, so scripts/check-view-chrome.js can
// require() it. CommonJS to match bookingRules.js, which router.js already
// imports the same way.
// -----------------------------------------------------------------------------

const R = require('./bookingRules')

// `desc` and `wasCalled` are read only by the home cards; the menu shows `title`
// alone. `testing` marks a page that is deployed but not yet open to everyone --
// see TESTING_ROLES below. No page is in testing now: จองวันส่งสินค้า left it
// on 2026-09-21, when shops moved their booking onto it. Putting a page back
// into testing is adding one `testing: true` line.
//
// Dropping the คิวรถเล็ก rename hint, once shops have stopped looking for the
// old name, is deleting one `wasCalled` line.
const PAGES = {
  index: {
    title: 'หน้าแรก', icon: 'mdi-home', routeName: 'index'
  },
  bookingDate: {
    title: 'จองวันส่งสินค้า', icon: 'mdi-calendar-check', routeName: 'bookingDate',
    desc: 'นัดหมายวันส่ง หรือค้นหาจากเลขที่ SO'
  },
  orderSearch: {
    title: 'ค้นหาการจอง', icon: 'mdi-magnify', routeName: 'orderSearch',
    desc: 'ค้นหาทุกการจองด้วยเบอร์โทรหรือเลขที่ SO และดูสถานะล่าสุด'
  },
  dispatchPlan: {
    title: 'จัดรถอัตโนมัติ', icon: 'mdi-robot', routeName: 'dispatchPlan',
    desc: 'ให้ระบบจัดรถและลำดับการส่งอัตโนมัติ'
  },
  carBooking: {
    title: 'คิวรถเล็ก', icon: 'mdi-bus-clock', routeName: 'carBooking',
    desc: 'จองคิวรถเล็กรายช่วงเวลา',
    wasCalled: 'จองรถ'
  },
  carSchedule: {
    title: 'ตารางรถ', icon: 'mdi-bus-marker', routeName: 'carSchedule',
    desc: 'ดูตารางรถประจำวัน'
  },
  managecar: {
    title: 'จัดรถ', icon: 'mdi-bus-wrench', routeName: 'managecar',
    desc: 'แก้ไขการจัดรถและเส้นทาง'
  },
  managecarinfo: {
    title: 'ข้อมูลการจัดรถ', icon: 'mdi-file-chart', routeName: 'managecarinfo',
    desc: 'สรุปข้อมูลการจัดรถ'
  },
  monitor: {
    title: 'กระดานติดตามรถ', icon: 'mdi-map-marker-path', routeName: 'monitor',
    desc: 'ดูความคืบหน้าและตำแหน่งรถระหว่างวัน'
  },
  upcountry: {
    title: 'รถใหญ่ต่างจังหวัด', icon: 'mdi-truck', routeName: 'upcountry',
    desc: 'ดูออเดอร์ต่างจังหวัดแต่ละภาค จัดรอบรถใหญ่ และส่งต่อให้จัดรถ'
  },
  psn: {
    title: 'ผู้ใช้งานระบบ', icon: 'mdi-account-badge', routeName: 'psn',
    desc: 'จัดการผู้ใช้งานและสิทธิ์'
  },
  drivers: {
    title: 'พนักงานขับรถ', icon: 'mdi-card-account-details', routeName: 'drivers',
    desc: 'เปิดหรือปิดการใช้งานแอปพนักงานขับรถ ตามรายชื่อใน SAP'
  }
}

// The page a `testing: true` entry is not yet open to anyone outside of. Reusing
// STAFF_ROLES rather than writing a fifth copy of the same four role names: the
// people who may test an unreleased booking screen are the people bookingRules
// already calls staff, which is also exactly who dispatchPlan is gated to.
const TESTING_ROLES = R.STAFF_ROLES

const STAFF_PAGES = [
  'index', 'bookingDate', 'orderSearch', 'dispatchPlan', 'upcountry', 'carBooking',
  'carSchedule', 'managecar', 'managecarinfo', 'monitor'
]

// A shop sees these three pages and nothing else: it books on จองวันส่งสินค้า, and
// only views and searches คิวรถเล็ก (carBooking.vue hides and refuses every
// booking write to a non-staff role -- its canBookHere). ค้นหาการจอง is every role's:
// it reads any customer's order by exact phone or SO (see the Security section of
// CLAUDE.md). จัดรถอัตโนมัติ is absent here AND gated in router.js -- check-view-chrome.js
// asserts both, because a shop reaching the dispatch planner would rewrite the whole day's plan.
const SHOP_PAGES = ['index', 'bookingDate', 'orderSearch', 'carBooking']

const ROLE_PAGES = {
  administrator: STAFF_PAGES.concat(['psn', 'drivers']),
  admin: STAFF_PAGES.concat(['psn', 'drivers']),
  manager: STAFF_PAGES,
  delivery: STAFF_PAGES,
  user: SHOP_PAGES,
  shop: SHOP_PAGES
}

// The pages a role may see listed, in menu order. An unknown role gets nothing,
// which is what sends it back to the login screen.
function pagesForRole (role) {
  return (ROLE_PAGES[role] || []).map(function (key) { return PAGES[key] })
}

// Seeing a page listed and being allowed into it are different questions while a
// page is in testing: a shop sees จองวันส่งสินค้า greyed out so the coming change
// is no surprise, but cannot open it.
function canOpen (page, role) {
  if (!page || !page.testing) return true
  return TESTING_ROLES.indexOf(role) !== -1
}

// The router asks by route name. A name this table does not know -- track, pc,
// the driver screens, the print-only report -- is not a menu page and is governed
// by its own meta.roles, so it passes.
function canOpenRoute (routeName, role) {
  const page = PAGES[routeName]
  if (!page) return true
  return canOpen(page, role)
}

// Where a signed-out visitor was going, carried through the login screen as
// ?redirect= so the page they asked for is where they land. Every cold load goes
// through login -- the store starts with no user and Firebase answers after the
// router's first guard -- so without this a link from kioskonlineshop
// (#/ordersearch?q=, #/bookingdate?q=, #/bookingdate?appt=1&...) ended on index
// with its query gone.
//
// The value arrives in a URL, so it is untrusted: only an in-app path is kept.
// '/' and the login screen say nothing (index is the default anyway), and a
// driver page is left to the driver routing, which has its own home.
const REDIRECT_MAX = 4000

function isDriverPath (path) {
  return path === '/driver' || path.indexOf('/driver/') === 0 ||
    path.indexOf('/driver?') === 0
}

function safeRedirect (value) {
  if (typeof value !== 'string') return null
  const path = value.trim()
  if (!path || path.length > REDIRECT_MAX) return null
  if (path.charAt(0) !== '/' || path.charAt(1) === '/' || path.charAt(1) === '\\') return null
  const bare = path.split(/[?#]/)[0]
  if (bare === '/' || bare === '/login' || bare.indexOf('/login/') === 0) return null
  if (isDriverPath(path)) return null
  return path
}

module.exports = {
  PAGES, ROLE_PAGES, TESTING_ROLES, pagesForRole, canOpen, canOpenRoute,
  safeRedirect
}
